import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Video } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';

const CALENDLY_URL = "https://calendly.com/lolcorp-uk/30min?hide_landing_page_details=1&hide_gdpr_banner=1&text_color=003333&primary_color=003333";

export const ConsultationPage = () => {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="consultation-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#003333]/60 hover:text-[#003333] mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
              Schedule a Call
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.9] text-[#003333] mb-6">
              Book Your<br />Consultation
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl">
              Let's discuss your project requirements and explore how LOLCorp can help transform 
              your internal systems. Our consultations are free, confidential, and focused on 
              understanding your unique challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#003333] mb-8">
                What to Expect
              </h2>
              
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#F8FAFC] rounded-xl flex items-center justify-center text-[#003333]">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#003333] text-lg mb-2">
                      30-Minute Discovery Call
                    </h3>
                    <p className="text-slate-600">
                      A focused session to understand your business challenges and technical requirements.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#F8FAFC] rounded-xl flex items-center justify-center text-[#003333]">
                    <Video className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#003333] text-lg mb-2">
                      Video or Phone Call
                    </h3>
                    <p className="text-slate-600">
                      Choose your preferred communication method. We'll send you a meeting link after booking.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#F8FAFC] rounded-xl flex items-center justify-center text-[#003333]">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#003333] text-lg mb-2">
                      No Obligation
                    </h3>
                    <p className="text-slate-600">
                      This is a free consultation to explore possibilities. No sales pressure, just genuine advice.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12 p-6 bg-[#003333] rounded-xl">
                <h3 className="text-white font-semibold mb-3">What We'll Discuss</h3>
                <ul className="space-y-2 text-white/80 text-sm">
                  <li>• Your current systems and pain points</li>
                  <li>• Project scope and timeline expectations</li>
                  <li>• Technical approach and architecture</li>
                  <li>• Budget considerations and ROI</li>
                  <li>• Next steps if there's a fit</li>
                </ul>
              </div>
            </motion.div>
            
            {/* Right Content - Calendly Embed */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-xl"
            >
              <div 
                className="calendly-inline-widget" 
                data-url={CALENDLY_URL}
                style={{ minWidth: '320px', height: '700px' }}
                data-testid="calendly-widget"
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ConsultationPage;
